import React from "react";
import {
  FileText,
  Eye,
  Edit2,
  ChevronLeft,
  ChevronRight,
  XCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Prescription,
  usePrescriptionsState,
} from "@/lib/dashboard/prescriptionsData";
import Skeleton from "@mui/material/Skeleton";
import { getVisiblePages } from "@/lib/utils";
import { checkUserPermissions } from "@/utils/checkUserPermissions";

import { accessTitanversePrescriptionView } from "@/data/constantStrings";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
interface RecentPrescriptionsProps {
  prescriptions: Prescription[];
  isDarkMode: boolean;
  onViewPrescription: (prescription: Prescription) => void;
  onEditPrescription: (prescription: Prescription) => void;
}

export function RecentPrescriptions({
  prescriptions,
  isDarkMode,
  onViewPrescription,
  onEditPrescription,
}: RecentPrescriptionsProps) {
  const {
    patientPrescriptionsLoader,
    paginatedPrescriptions,
    totalPages,
    currentPage,
    itemsPerPage,
    handlePageChange,
  } = usePrescriptionsState();
  const hasPrescriptionPermission = checkUserPermissions(
    accessTitanversePrescriptionView,
  );

  const [expandedDeclineIds, setExpandedDeclineIds] = React.useState<
    Set<number>
  >(new Set());

  const toggleDeclineDetails = (id: number) => {
    const newSet = new Set(expandedDeclineIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedDeclineIds(newSet);
  };

  return (
    <div
      className={`rounded-xl p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      } shadow-sm`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Recent Prescriptions
          </h3>
        </div>
        <Badge
          variant="secondary"
          className={`text-xs ${
            isDarkMode
              ? "bg-gray-700 text-gray-300"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {patientPrescriptionsLoader ? "..." : prescriptions.length}
        </Badge>
      </div>

      <div className="space-y-3">
        {patientPrescriptionsLoader ? (
          // 🔹 Skeleton placeholders
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={`border rounded-lg p-3 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <Skeleton
                  variant="circular"
                  width={32}
                  height={32}
                  sx={{ bgcolor: isDarkMode ? "grey.700" : "grey.300" }}
                />
                <div className="flex-1 space-y-2">
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={18}
                    sx={{ bgcolor: isDarkMode ? "grey.700" : "grey.300" }}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={16}
                    sx={{ bgcolor: isDarkMode ? "grey.700" : "grey.300" }}
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={14}
                    sx={{ bgcolor: isDarkMode ? "grey.700" : "grey.300" }}
                  />
                </div>
              </div>
            </div>
          ))
        ) : prescriptions.length === 0 ? (
          <div
            className={`text-center py-6 border rounded-lg transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-gray-400"
                : "bg-gray-50 border-gray-200 text-gray-500"
            }`}
          >
            <FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No prescriptions found</p>
          </div>
        ) : (
          // 🔹 Real prescriptions with pagination
          paginatedPrescriptions.map((prescription) => (
            <div
              key={prescription.id}
              className={`border rounded-lg p-3 group transition-colors duration-300 ${
                prescription.status === "Rejected"
                  ? isDarkMode
                    ? "border-red-900 bg-red-950/20"
                    : "border-red-200 bg-red-50/30"
                  : isDarkMode
                    ? "border-gray-600 hover:bg-gray-700"
                    : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                    prescription.status === "Rejected"
                      ? isDarkMode
                        ? "bg-red-900/30"
                        : "bg-red-100"
                      : isDarkMode
                        ? "bg-blue-900/30"
                        : "bg-blue-100"
                  }`}
                >
                  <FileText
                    className={`w-4 h-4 transition-colors duration-300 ${
                      prescription.status === "Rejected"
                        ? isDarkMode
                          ? "text-red-400"
                          : "text-red-600"
                        : isDarkMode
                          ? "text-blue-400"
                          : "text-blue-600"
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {prescription.number}
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={`text-xs ${
                          prescription.status === "Approved"
                            ? "text-green-600"
                            : prescription.status === "Pending"
                              ? "text-blue-600"
                              : prescription.status === "Rejected"
                                ? "bg-red-100 text-red-700 border-red-300"
                                : "text-red-600"
                        }`}
                      >
                        {prescription.status === "Rejected" ? (
                          <div className="flex items-center gap-1">
                            <XCircle className="w-3 h-3" />
                            <span>Declined</span>
                          </div>
                        ) : (
                          prescription.status
                        )}
                      </Badge>
                      <div className="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-6 h-6 p-0"
                          title="View Prescription"
                          onClick={() => {
                            if (!hasPrescriptionPermission) {
                              setShowPermissionMessage("View prescription");
                              setShowPermissionModal(true);
                              return;
                            }

                            onViewPrescription(prescription);
                          }}
                        >
                          <Eye className="w-3 h-3 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled
                          className="w-6 h-6 p-0"
                          onClick={() => onEditPrescription(prescription)}
                          title="Edit Prescription"
                        >
                          <Edit2 className="w-3 h-3 text-gray-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p
                    className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {prescription.medication}
                  </p>
                  <p
                    className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {prescription.date}
                  </p>
                  {prescription?.status === "Rejected" && (
                    <div
                      className={`mt-3 pt-3 border-t ${
                        isDarkMode ? "border-red-900/50" : "border-red-100"
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-1 text-red-600">
                        <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span className="text-[11px] font-semibold leading-tight">
                          {prescription?.declineReason ||
                            "Prescription Declined"}
                        </span>
                      </div>

                      <button
                        onClick={() => toggleDeclineDetails(prescription?.id)}
                        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 mb-1.5 ml-5.5 transition-colors"
                      >
                        {expandedDeclineIds.has(prescription?.id) ? (
                          <>
                            <ChevronUp className="w-2.5 h-2.5" />
                            <span>Hide details</span>
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-2.5 h-2.5" />
                            <span>Show details</span>
                          </>
                        )}
                      </button>

                      {expandedDeclineIds.has(prescription?.id) &&
                        prescription.declineComment && (
                          <p
                            className={`text-[10px] leading-relaxed ml-5.5 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {prescription.declineComment}
                          </p>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!patientPrescriptionsLoader &&
        prescriptions.length > itemsPerPage &&
        totalPages > 1 && (
          <div
            className={`flex items-center justify-between mt-4 pt-3 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div
              className={`text-xs ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, prescriptions.length)} of{" "}
              {prescriptions.length}
            </div>

            <div className="flex flex-wrap items-center max-w-full gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-1 rounded transition-colors duration-300 ${
                  currentPage === 1
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {(() => {
                const { start, end } = getVisiblePages(
                  currentPage,
                  totalPages,
                  5,
                ); // max 5 visible

                return (
                  <>
                    {start > 1 && (
                      <>
                        <button
                          onClick={() => handlePageChange(1)}
                          className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                        >
                          1
                        </button>
                        <span className="px-1 text-xs text-gray-400">…</span>
                      </>
                    )}

                    {Array.from(
                      { length: end - start + 1 },
                      (_, i) => start + i,
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          page === currentPage
                            ? isDarkMode
                              ? "bg-blue-600 text-white"
                              : "bg-blue-500 text-white"
                            : isDarkMode
                              ? "text-gray-300 hover:bg-gray-700"
                              : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    {end < totalPages && (
                      <>
                        <span className="px-1 text-xs text-gray-400">…</span>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </>
                );
              })()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-1 rounded transition-colors duration-300 ${
                  currentPage === totalPages
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
    </div>
  );
}
